// 🎯 Componente para finalizar productos procesados con confirmación
// Component for finishing processed products with confirmation
import { useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';

export interface FinishProcessedButtonProps {
  onFinish?: () => void;
  onLocate?: () => void;
  className?: string;
  disabled?: boolean;
  status?: number; // 1: Abierto, 2: Por ubicar, 0: Cerrado
  labelOverride?: string;
  onClickOverride?: () => void;
}

export const FinishProcessedButton = ({
  onFinish,
  onLocate,
  className = '',
  disabled = false,
  status,
  labelOverride,
  onClickOverride,
}: FinishProcessedButtonProps) => {
  const { t } = useTranslation('receiveModule');
  const confirmModalRef = useRef<ConfirmModalRef>(null);

  // Determinar label del botón según estado
  const buttonLabel = useMemo(() => {
    if (labelOverride) return labelOverride;
    if (status === 1) return t('buttons.finish');
    if (status === 2) return t('ui.locate_button');
    return '';
  }, [status, t, labelOverride]);

  // 🎯 Acción principal del botón
  const handleFinishProcessed = useCallback(() => {
    if (onClickOverride) {
      onClickOverride();
      return;
    }
    // Si el estado es "Por ubicar" redirigimos al modo de ubicación
    if (status === 2) {
      onLocate?.();
      return;
    }
    confirmModalRef.current?.open(
      onFinish || (() => {}),
      undefined,
      t('modal.finish.title'),
      t('modal.finish.message'),
      t('modal.finish.confirm'),
      t('modal.finish.cancel'),
    );
  }, [t, onFinish, onLocate, status, onClickOverride]);

  // Si está cerrado (0), no renderizar botón
  if (status === 0 && !labelOverride) {
    return null;
  }

  return (
    <>
      <Button
        variant="primary"
        size="lg"
        onClick={handleFinishProcessed}
        className={`shrink-0 ${className}`}
        data-testid="finish-processed-button"
        disabled={disabled}
      >
        {buttonLabel}
      </Button>

      <ConfirmModal ref={confirmModalRef} />
    </>
  );
};
